/**
 * Flatten GET /bills/:id JSON into the bill object Pro detail already consumes.
 *
 * Authenticated detail returns { success, data: { bill, sponsors, lobbyists, aiSummary, ... } }.
 * Taking only `data` leaves bill.id / title undefined and makes position save/clear no-op.
 * Flat / public-shaped payloads (id or number on the envelope) pass through with light aliases.
 */

export type ProBillDetailRecord = Record<string, unknown> & {
  id?: string | number;
  billNumber?: string;
  number?: string;
  title?: string;
  sponsors?: unknown[];
  committees?: unknown[];
  lobbyists?: unknown[];
  aiSummary?: unknown;
  aiShortSummary?: string | null;
  introducedDate?: string | null;
  lastActivity?: string | null;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function aliasBillFields(bill: Record<string, unknown>): ProBillDetailRecord {
  const aiSummary = asRecord(bill.aiSummary);
  return {
    ...bill,
    billNumber:
      (bill.billNumber as string | undefined) ??
      (bill.bill_number as string | undefined) ??
      (bill.number as string | undefined),
    introducedDate:
      (bill.introducedDate as string | null | undefined) ??
      (bill.introduced_date as string | null | undefined) ??
      null,
    lastActivity:
      (bill.lastActivity as string | null | undefined) ??
      (bill.latest_action as string | null | undefined) ??
      (bill.last_activity as string | null | undefined) ??
      null,
    aiShortSummary:
      (bill.aiShortSummary as string | null | undefined) ??
      (aiSummary?.short_summary as string | null | undefined) ??
      null,
  };
}

/**
 * @param responseData Axios `response.data` (or an already-unwrapped body).
 */
export function unwrapProBillDetail(responseData: unknown): ProBillDetailRecord | null {
  const root = asRecord(responseData);
  if (!root) return null;

  // Prefer nested success envelope when present; otherwise treat root as the body.
  const body = asRecord(root.data) ?? root;
  const nestedBill = asRecord(body.bill);

  if (nestedBill) {
    const aiSummary = body.aiSummary ?? nestedBill.aiSummary ?? null;
    return aliasBillFields({
      ...nestedBill,
      sponsors: (body.sponsors as unknown[]) ?? (nestedBill.sponsors as unknown[]) ?? [],
      committees: (body.committees as unknown[]) ?? (nestedBill.committees as unknown[]) ?? [],
      lobbyists: (body.lobbyists as unknown[]) ?? (nestedBill.lobbyists as unknown[]) ?? [],
      aiSummary,
      nextCalendarEvent:
        body.nextCalendarEvent ?? nestedBill.nextCalendarEvent ?? null,
    });
  }

  // Already-flat bill (tests, list-shaped cache, public-style payloads).
  if (body.id != null || body.number != null || body.billNumber != null || body.bill_number != null) {
    return aliasBillFields(body);
  }

  return null;
}
